// store/modules/companies.js
import api from '@/api/api'

export default {
    namespaced: true,
    state: {
        companies: [],
        currentCompany: null,
        loading: false,
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: 50
        },
        search: ''
    },
    getters: {
        allCompanies: (state) => state.companies,
        currentCompany: (state) => state.currentCompany,
        loading: (state) => state.loading,
        error: (state) => state.error,
        pagination: (state) => state.pagination,
        search: (state) => state.search
    },
    mutations: {
        SET_COMPANIES(state, companies) {
            state.companies = companies
        },
        SET_CURRENT_COMPANY(state, company) {
            state.currentCompany = company
        },
        ADD_COMPANY(state, company) {
            state.companies.push(company)
        },
        UPDATE_COMPANY(state, updatedCompany) {
            const index = state.companies.findIndex(company => company._id === updatedCompany._id)
            if (index !== -1) {
                state.companies.splice(index, 1, updatedCompany)
            }
        },
        REMOVE_COMPANY(state, id) {
            state.companies = state.companies.filter(company => company._id !== id)
        },
        SET_LOADING(state, status) {
            state.loading = status
        },
        SET_ERROR(state, error) {
            state.error = error
        },
        CLEAR_ERROR(state) {
            state.error = null
        },
        SET_PAGINATION(state, pagination) {
            state.pagination = pagination
        },
        SET_SEARCH(state, search) {
            state.search = search
        }
    },
    actions: {
        // Fetch company data from LinkedIn URL
        async fetchCompanyFromLinkedIn({ commit }, linkedinUrl) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.post('/companies/linkedin', { linkedinUrl })

                return response.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch company data from LinkedIn'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Get company logo URL
        async getCompanyLogoUrl({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/companies/${id}/logo`)

                return response.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to get company logo URL'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Create a new company
        async createCompany({ commit }, formData) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.post('/companies', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })

                // Only add to the local state if it's a new company
                if (!response.data.existing) {
                    commit('ADD_COMPANY', response.data.data)
                }

                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to create company'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch all companies
        async fetchCompanies({ commit, state }, { page, search } = {}) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                if (search !== undefined) commit('SET_SEARCH', search)

                const currentPage = page || state.pagination.currentPage

                const params = {
                    page: currentPage,
                    limit: state.pagination.limit,
                    search: state.search
                }

                const response = await api.get('/companies', { params })

                commit('SET_COMPANIES', response.data.data)
                commit('SET_PAGINATION', {
                    currentPage: response.data.currentPage,
                    totalPages: response.data.totalPages,
                    total: response.data.total,
                    limit: state.pagination.limit
                })

                return response.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch companies'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Fetch a single company by ID
        async fetchCompanyById({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.get(`/companies/${id}`)
                commit('SET_CURRENT_COMPANY', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch company'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Update a company
        async updateCompany({ commit }, { id, formData }) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                const response = await api.put(`/companies/${id}`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })

                commit('UPDATE_COMPANY', response.data.data)
                commit('SET_CURRENT_COMPANY', response.data.data)
                return response.data.data
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to update company'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Delete a company
        async deleteCompany({ commit }, id) {
            try {
                commit('SET_LOADING', true)
                commit('CLEAR_ERROR')

                await api.delete(`/companies/${id}`)
                commit('REMOVE_COMPANY', id)
                return true
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to delete company'
                commit('SET_ERROR', errorMessage)
                throw new Error(errorMessage)
            } finally {
                commit('SET_LOADING', false)
            }
        },

        // Change page
        async changePage({ dispatch }, page) {
            return dispatch('fetchCompanies', { page })
        },

        // Search companies
        async searchCompanies({ dispatch }, search) {
            return dispatch('fetchCompanies', { search, page: 1 })
        }
    }
}
